/**
 * WHAT JUST ARRIVED on an item's page, so it can glow for a moment.
 *
 * The page reloads itself whenever the board changes, and during a dig it
 * changed with nothing to say what was new: a row appeared somewhere in a long
 * page and you had to spot it. Each reload's item ids are compared with the
 * last one's, for the same item; ids that were not there before are "fresh"
 * for `GLOW_MS`, and every row that draws one wears `FRESH_CLASS` until then.
 *
 * The first load of a page marks nothing: everything on it is new to you, and
 * a page that lights up whole says nothing.
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';

export const GLOW_MS = 4000;
export const FRESH_CLASS = 'bg-amber-100 dark:bg-amber-900/40';
/** On every row that can glow, so the colour fades out rather than snapping. */
export const GLOW_BASE = 'transition-colors duration-1000';

/** Every numeric `id` anywhere in a value: each row a page draws carries one. Pure. */
export function collectIds(value: unknown, out: Set<number> = new Set()): Set<number> {
  if (Array.isArray(value)) {
    for (const v of value) collectIds(v, out);
  } else if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o.id === 'number') out.add(o.id);
    for (const v of Object.values(o)) {
      if (v && typeof v === 'object') collectIds(v, out);
    }
  }
  return out;
}

/** The ids in `next` that `prev` did not have. Nothing on a first look. Pure. */
export function arrivals(prev: ReadonlySet<number> | null, next: ReadonlySet<number>): Set<number> {
  const out = new Set<number>();
  if (!prev) return out;
  next.forEach(id => {
    if (!prev.has(id)) out.add(id);
  });
  return out;
}

export const FreshIds = createContext<ReadonlySet<number>>(new Set());

/** The class for one row: glowing while its id is fresh. */
export function useFreshClass(): (id: number) => string {
  const fresh = useContext(FreshIds);
  return id => `${GLOW_BASE} ${fresh.has(id) ? FRESH_CLASS : ''}`;
}

/**
 * The fresh ids for a page about `itemId`, recomputed each time `data` changes.
 * Each arrival glows for its own `GLOW_MS`, so a burst of reloads during a dig
 * does not cut an earlier one short.
 */
export function useArrivals(itemId: number, data: unknown): ReadonlySet<number> {
  const seen = useRef<{ item: number; ids: Set<number> } | null>(null);
  const until = useRef(new Map<number, number>());
  const alive = useRef(true);
  const [, setTick] = useState(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    const ids = collectIds(data);
    const was = seen.current && seen.current.item === itemId ? seen.current.ids : null;
    if (!was) until.current.clear();
    seen.current = { item: itemId, ids };
    const got = arrivals(was, ids);
    got.delete(itemId);
    if (!got.size) return;
    const end = Date.now() + GLOW_MS;
    got.forEach(id => until.current.set(id, end));
    setTick(t => t + 1);
    // Not cleared on the next reload: each arrival keeps its own time.
    window.setTimeout(() => {
      if (alive.current) setTick(t => t + 1);
    }, GLOW_MS + 50);
  }, [itemId, data]);

  const now = Date.now();
  const fresh = new Set<number>();
  until.current.forEach((end, id) => {
    if (end > now) fresh.add(id);
    else until.current.delete(id);
  });
  return fresh;
}
